// FindExistingSection.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Icons
import Icon from 'svg/Icon';

// Utilities
import { getNodeTypeLabelFromDefinition } from 'utilities/NodeTypeLabels';

// CSS
import './FindExistingSection.css';

// Components
import TextInput from 'components/textinput/TextInput';

/**
 * FindExistingSection
 * 
 * Renders the UI for finding and attaching existing nodes in the Add Modal.
 * 
 * @param {string} searchTerm - The current search term entered by the user.
 * @param {function} setSearchTerm - Function to update the search term state.
 * @param {string} searchError - Any error message resulting from the search operation.
 * @param {boolean} searching - Indicates whether a search operation is currently in progress.
 * @param {boolean} noResults - Indicates whether the search returned no results.
 * @param {Array} searchResults - The array of nodes that match the search criteria.
 * @param {Map} availableTypeMap - A map of type keys to type definitions for displaying type information in results.
 * @param {string} anchorNodeKey - The key of the anchor node to which an existing node may be attached.
 * @param {string} attachingNodeKey - The key of the node currently being attached, used for UI feedback.
 * @param {function} onAttachExisting - Handler function to call when an existing node is selected for attachment.
 * @param {boolean} enableSelectionWithoutAnchor - Enables selection even when no anchor node is provided.
 * 
 * @returns {ReactNode} The rendered component.
 */
const FindExistingSection = React.memo(({
  searchTerm,
  setSearchTerm,
  searchError,
  searching,
  noResults,
  searchResults,
  availableTypeMap,
  anchorNodeKey,
  attachingNodeKey,
  onAttachExisting,
  hideInput = false,
  enableSelectionWithoutAnchor = false,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context
  const { theme } = useTheme();
  const [hoveredResultKey, setHoveredResultKey] = React.useState('');

  return (

    <div className="find-existing-section-container">

      {!hideInput && (
        <div className="find-existing-section-controls">
          <TextInput
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search existing..."
            autoFocus
          />
        </div>
      )}

      {/* SEARCH ERROR MESSAGE */}
      {searchError && (
        <div
          className="find-existing-section-help-text"
          style={{ color: theme.foregroundMuted }}
        >
          {searchError}
        </div>
      )}

      {/* SEARCHING MESSAGE */}
      {searching && (
        <div
          className="find-existing-section-help-text"
          style={{ color: theme.foregroundMuted }}
        >
          Searching...
        </div>
      )}

      {/* NO RESULTS MESSAGE */}
      {noResults && (
        <div
          className="find-existing-section-help-text"
          style={{ color: theme.foregroundMuted }}
        >
          No results found.
        </div>
      )}

      {/* SEARCH RESULTS */}
      <div className="find-existing-section-search-results">

        {(searchResults || []).map((result) => {

          // Type definition for the result
          const typeDef = result?.type ? availableTypeMap.get(result.type) : null;

          // Label to display for the type of the result
          const rowTypeLabel = getNodeTypeLabelFromDefinition(typeDef, result?.type, { fallback: 'Node' });

          // Determine if the search result should be disabled (not clickable) based on whether 
          // it is the anchor node, or if it is currently being attached
          const isDisabled = (!enableSelectionWithoutAnchor && !anchorNodeKey)
            || result?.key === anchorNodeKey
            || attachingNodeKey === result?.key;
          const resultKey = String(result?.key || '');
          const isHovered = hoveredResultKey === resultKey;

          return (

            <div key={result?.key}
              className="find-existing-section-search-row"
              style={{
                opacity: isDisabled ? 0.6 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                backgroundColor: !isDisabled && isHovered ? theme.backgroundHover : 'transparent',
                borderColor: !isDisabled && isHovered ? theme.foregroundMuted : theme.backgroundMuted,
              }}
              onClick={() => {
                if (!isDisabled) onAttachExisting(result);
              }}
              onMouseEnter={() => {
                if (!isDisabled) setHoveredResultKey(resultKey);
              }}
              onMouseLeave={() => setHoveredResultKey('')}
            >
              {/* ICON */}
              <div className="find-existing-section-search-row-icon">
                <Icon
                  iconKey={typeDef?.iconKey || 'deployed_code'}
                  width={24}
                  height={24}
                  color={theme.foregroundMuted}
                />
              </div>

              {/* TEXT */}
              <div className="find-existing-section-search-row-text">

                {/* TITLE */}
                <div
                  className="find-existing-section-search-row-title"
                  style={{ color: theme.foreground }}
                >
                  {result?.title || 'Untitled'}
                </div>

                {/* TYPE */}
                <div
                  className="find-existing-section-search-row-type"
                  style={{ color: theme.foregroundMuted }}
                >
                  {rowTypeLabel}
                </div>

              </div>

            </div>

          );

        })}

      </div>

    </div>

  );
});

FindExistingSection.displayName = 'FindExistingSection';

export default FindExistingSection;
